import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CurrentSpaceContextProvider } from "@/react/contexts";
import { AdvancedOptions } from "@circle-react/components/EventsV3/Modal/Tabs/AdvancedOptions";
import { BasicInfo } from "@circle-react/components/EventsV3/Modal/Tabs/BasicInfo";
import { Details } from "@circle-react/components/EventsV3/Modal/Tabs/Details";
import { Email } from "@circle-react/components/EventsV3/Modal/Tabs/Email";
import { Notifications } from "@circle-react/components/EventsV3/Modal/Tabs/Notifications";
import { Overview } from "@circle-react/components/EventsV3/Modal/Tabs/Overview";
import { People } from "@circle-react/components/EventsV3/Modal/Tabs/People";
import type { Event, FormScheduleValues } from "@circle-react/types";
import { useCustomizeUIData } from "@circle-react-shared/CustomizeUI";
import { SECTIONS } from "../../utils";

interface EventsBodyProps {
  event: Event;
  setFormScheduleValues: (values: FormScheduleValues) => void;
  setIsDirty: (isDirty: boolean) => void;
  setLocationType: (locationType: string) => void;
  setPaywallAttributesDirty: (paywallAttributesDirty: boolean) => void;
  setRepeatsDirty: (repeatsDirty: boolean) => void;
  setStartsAtDirty: (startsAtDirty: boolean) => void;
  setValueRef: any;
  triggerValidationRef: any;
}

export const EventsBody = ({
  event,
  triggerValidationRef,
  setValueRef,
  setIsDirty,
  setLocationType,
  setStartsAtDirty,
  setPaywallAttributesDirty,
  setRepeatsDirty,
  setFormScheduleValues,
}: EventsBodyProps) => {
  const { currentSection } = useCustomizeUIData();
  const {
    trigger,
    setValue,
    formState: { dirtyFields },
  } = useFormContext();

  // Watch form values for starts_at, ends_at, and frequency
  const startsAt = useWatch({ name: "event_setting_attributes.starts_at" });
  const endsAt = useWatch({ name: "event_setting_attributes.ends_at" });
  const frequency = useWatch({
    name: "recurring_setting_attributes.frequency",
  });

  // Update form values when they change
  useEffect(() => {
    // Convert endsAt to string if it's a Date object
    const formatEndsAt = (value: any) => {
      if (value instanceof Date) {
        return value.toUTCString();
      }
      return value;
    };

    const formValues = {
      startsAt: startsAt || event?.event_setting_attributes?.starts_at || "",
      endsAt:
        formatEndsAt(endsAt) ||
        formatEndsAt(event?.event_setting_attributes?.ends_at) ||
        "",
      frequency:
        frequency || event?.recurring_setting_attributes?.frequency || "",
    };
    setFormScheduleValues(formValues);
  }, [startsAt, endsAt, frequency, event, setFormScheduleValues]);

  useEffect(() => {
    if (triggerValidationRef && trigger) {
      triggerValidationRef.current = trigger;
    }
  }, [triggerValidationRef, trigger]);

  useEffect(() => {
    if (setValueRef && setValue) {
      setValueRef.current = setValue;
    }
  }, [setValueRef, setValue]);

  // Note we need to generate a new array every time in order for useEffect to execute
  // otherwise the dirtyFields value (reference) won't change and this won't work
  const dirtyValues = Object.values(dirtyFields);
  const didStartsAtChanged = !!dirtyFields?.event_setting_attributes?.starts_at;
  const didPaywallAttributesChanged =
    !!dirtyFields?.paywall_attributes?.price_amount ||
    !!dirtyFields?.paywall_attributes?.description;
  const didRepeatChanged =
    !!dirtyFields?.recurring_setting_attributes?.frequency;
  useEffect(() => {
    const isDirty = dirtyValues?.some(value => !!value);
    setIsDirty(isDirty);
  }, [setIsDirty, dirtyValues]);

  useEffect(() => {
    setStartsAtDirty(didStartsAtChanged);
  }, [setStartsAtDirty, didStartsAtChanged]);

  useEffect(() => {
    setPaywallAttributesDirty(didPaywallAttributesChanged);
  }, [setPaywallAttributesDirty, didPaywallAttributesChanged]);

  useEffect(() => {
    setRepeatsDirty(didRepeatChanged);
  }, [setRepeatsDirty, didRepeatChanged]);

  return (
    <div className="p-4 md:p-6 lg:py-8">
      {currentSection === SECTIONS.overview && <Overview event={event} />}
      {currentSection === SECTIONS.people && (
        <CurrentSpaceContextProvider spaceId={event?.space_id}>
          <People event={event} />
        </CurrentSpaceContextProvider>
      )}
      {currentSection === SECTIONS.basicInfo && (
        <BasicInfo event={event} setLocationType={setLocationType} />
      )}
      {currentSection === SECTIONS.details && <Details />}
      {currentSection === SECTIONS.notifications && (
        <Notifications event={event} />
      )}
      {currentSection === SECTIONS.email && <Email event={event} />}
      {currentSection === SECTIONS.advancedOptions && (
        <AdvancedOptions event={event} />
      )}
    </div>
  );
};
